(function(window, document) {
    'use strict';

    const AdvertisementWidget = {
        config: {
            appBaseUrl: (typeof process !== 'undefined' && process.env && process.env.MIX_APP_URL)
                ? process.env.MIX_APP_URL
                : 'https://app.spheriq.ch',
            apiBaseUrl: (typeof process !== 'undefined' && process.env && process.env.MIX_APP_URL)
                ? (process.env.MIX_APP_URL + '/api/advertisements')
                : 'https://app.spheriq.ch/api/advertisements',
            cookieName: 'advertisement_visitor_id',
            containerSelector: '.advertisement-container, [data-advertisement]',
            initialized: false
        },

        state: {
            effectiveType: null,
            language: null,
            subscription: null,
            location: null,
            cookieId: null,
            shownBanners: new Set(),
            observers: new Map(),
            banners: null,
            bannersLoading: false,
            bannersPromise: null
        },

        init: function(options = {}) {
            let hasEffectiveType = options.effectiveType !== undefined && options.effectiveType !== null && options.effectiveType !== '';
            let newEffectiveType = options.effectiveType || 0;
            let newLanguage = options.language !== undefined && options.language !== null && options.language !== '' ? options.language : null;
            let newSubscription = options.subscription !== undefined && options.subscription !== null && options.subscription !== '' ? options.subscription : 'guest';

            if (this.config.initialized) {
                if (
                    (hasEffectiveType && this.state.effectiveType !== newEffectiveType)
                    || this.state.language !== newLanguage
                    || this.state.subscription !== newSubscription
                ) {
                    this.state.effectiveType = newEffectiveType;
                    this.state.language = newLanguage;
                    this.state.subscription = newSubscription;
                    this.state.location = null;
                    this.state.banners = null;
                    this.state.bannersPromise = null;

                    this.initContainers();
                }

                return;
            }

            this.state.cookieId = this.getOrCreateCookieId();
            this.state.shownBanners = new Set();
            this.state.effectiveType = newEffectiveType;
            this.state.language = newLanguage;
            this.state.subscription = newSubscription;
            this.config.initialized = true;
            this.initContainers();
        },

        initContainers: function() {
            if (!this.config.initialized) {
                return;
            }

            const containers = document.querySelectorAll(this.config.containerSelector);
            if (containers.length === 0) {
                return;
            }

            const firstContainer = Array.from(containers).find((container) => container.dataset.location && container.dataset.format);
            if (!firstContainer) {
                return;
            }

            const location = firstContainer.dataset.location;

            if ((this.state.banners === null || this.state.location !== location) && this.state.bannersLoading) {
                if (this.state.bannersPromise) {
                    this.state.bannersPromise.then(() => {
                        this.initContainers();
                    });
                }
                return;
            }

            if (this.state.banners === null || this.state.location !== location) {
                this.loadAllBanners(location).then(() => {
                    this.initContainers();
                });
                return;
            }

            containers.forEach(container => {
                if (container.dataset.location && container.dataset.format) {
                    if (container.dataset.advertisementInitialized === 'true') {
                        return;
                    }
                    container.dataset.advertisementInitialized = 'true';
                    this.displayBanner(container, this.state.banners || [], container.dataset.location, container.dataset.format);
                }
            });
        },

        // Load banners for a specific location once per page
        loadAllBanners: function(location) {
            if (!location) {
                return Promise.resolve([]);
            }

            if (this.state.location === location && this.state.banners !== null) {
                return Promise.resolve(this.state.banners);
            }

            if (this.state.location === location && this.state.bannersLoading && this.state.bannersPromise) {
                return this.state.bannersPromise;
            }

            this.state.location = location;
            this.state.banners = null;
            this.state.bannersLoading = true;

            let effectiveType = this.state?.effectiveType || 0;
            let language = this.state?.language || null;
            let subscription = this.state?.subscription || 'guest';
            let params = new URLSearchParams({ effective_type: effectiveType, location: location });

            if (language) {
                params.set('language', language);
            }

            if (subscription) {
                params.set('subscription', subscription);
            }

            let apiUrl = this.config.apiBaseUrl + '/banners?' + params.toString();

            this.state.bannersPromise = fetch(apiUrl, {
                method: 'GET',
                credentials: 'same-origin',
                headers: {
                    'Accept': 'application/json'
                }
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch banners: ' + response.status);
                }

                return response.json();
            })
            .then(data => {
                this.state.banners = (data && data.success && data.data && Array.isArray(data.data)) ? data.data : [];
                this.state.bannersLoading = false;
                return this.state.banners;
            })
            .catch(error => {
                console.error('[AdvertisementWidget] Error loading banners!');
                this.state.banners = [];
                this.state.bannersLoading = false;
                return [];
            });

            return this.state.bannersPromise;
        },

        getOrCreateCookieId: function() {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.startsWith(this.config.cookieName + '=')) {
                    return cookie.substring(this.config.cookieName.length + 1);
                }
            }

            const array = new Uint8Array(32);
            if (window.crypto && window.crypto.getRandomValues) {
                window.crypto.getRandomValues(array);
            } else {
                // Fallback for older browsers
                for (let i = 0; i < array.length; i++) {
                    array[i] = Math.floor(Math.random() * 256);
                }
            }

            const cookieId = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
            const now = new Date();
            const endOfDay = new Date(Date.UTC(
                now.getUTCFullYear(),
                now.getUTCMonth(),
                now.getUTCDate() + 1,
                0, 0, 0, 0
            ));
            const maxAge = Math.floor((endOfDay.getTime() - now.getTime()) / 1000);

            document.cookie = `${this.config.cookieName}=${cookieId}; path=/; max-age=${maxAge}; SameSite=Lax`;
            return cookieId;
        },

        displayBanner: function(container, banners, location, format) {
            if (!banners || banners.length === 0) {
                container.style.display = 'none';
                return;
            }

            const formatFilteredBanners = banners.filter(banner => {
                return format === 'vertical'
                    ? banner.vertical_image_url !== null
                    : banner.horizontal_image_url !== null;
            });

            if (formatFilteredBanners.length === 0) {
                container.style.display = 'none';
                return;
            }

            const availableBanners = formatFilteredBanners.filter(banner =>
                !this.state.shownBanners.has(banner.id)
            );

            if (availableBanners.length === 0) {
                container.style.display = 'none';
                return;
            }

            const randomIndex = Math.floor(Math.random() * availableBanners.length);
            const selectedBanner = availableBanners[randomIndex];
            this.state.shownBanners.add(selectedBanner.id);
            this.renderBanner(container, selectedBanner, location, format);
        },

        renderBanner: function(container, banner, location, format) {
            const bannerElement = document.createElement('a');
            let clickUrl = banner.click_url || (this.config.appBaseUrl + '/advertisements/click/' + banner.id + '?location=' + encodeURIComponent(location));
            if (this.state.cookieId) {
                clickUrl += (clickUrl.includes('?') ? '&' : '?') + 'cookie_id=' + encodeURIComponent(this.state.cookieId);
            }
            bannerElement.href = clickUrl;
            bannerElement.target = '_blank';
            bannerElement.rel = 'noopener noreferrer';
            bannerElement.referrerPolicy = 'no-referrer';
            bannerElement.className = 'advertisement-banner';
            bannerElement.dataset.advertisementId = banner.id;
            bannerElement.dataset.location = location;

            const imageUrl = format === 'vertical'
                ? (banner.vertical_image_url || banner.horizontal_image_url)
                : (banner.horizontal_image_url || banner.vertical_image_url);

            if (!imageUrl) {
                container.style.display = 'none';
                return;
            }

            const img = document.createElement('img');
            img.alt = imageUrl;
            img.loading = 'lazy';
            img.referrerPolicy = 'no-referrer';
            img.style.maxWidth = '100%';
            img.style.width = '100%';
            img.style.height = 'auto';
            img.style.display = 'block';

            const self = this;

            const preload = new Image();
            preload.onload = function() {
                img.src = imageUrl;
                bannerElement.appendChild(img);
                container.querySelectorAll('.advertisement-banner').forEach(function (el) { el.remove(); });
                container.appendChild(bannerElement);
                container.style.display = '';
                self.trackImpression(bannerElement, banner.id, location);
            };
            preload.onerror = function() {
                container.style.display = 'none';
            };
            preload.src = imageUrl;
        },

        // Track impression when banner enters viewport (50% visible)
        trackImpression: function(element, advertisementId, location) {
            if (!('IntersectionObserver' in window)) {
                this.sendImpression(advertisementId, location);
                return;
            }

            if (element.dataset.impressionTracked === 'true') {
                return;
            }

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && entry.intersectionRatio > 0) {
                        this.sendImpression(advertisementId, location);
                        element.dataset.impressionTracked = 'true';
                        observer.unobserve(entry.target);
                        this.state.observers.delete(entry.target);
                    }
                });
            }, {
                threshold: 0.5
            });

            observer.observe(element);
            this.state.observers.set(element, observer);
        },

        sendImpression: function(advertisementId, location) {
            this.sendTrackingRequest('/impressions', {
                advertisement_id: advertisementId,
                location: location,
                cookie_id: this.state.cookieId
            }, 'impression');
        },

        trackClick: function(advertisementId, location) {
            this.sendTrackingRequest('/clicks', {
                advertisement_id: advertisementId,
                location: location,
                cookie_id: this.state.cookieId
            }, 'click');
        },

        // Send tracking request with retry logic (retries on 5xx errors only)
        sendTrackingRequest: function(endpoint, data, eventType, retryCount = 0) {
            const maxRetries = 2;
            const retryDelay = 2000;

            fetch(this.config.apiBaseUrl + endpoint, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(data)
            })
            .then(response => {
                if (!response.ok && response.status >= 500 && retryCount < maxRetries) {
                    throw new Error('RETRY_' + response.status);
                }
            })
            .catch(error => {
                if (error.message.startsWith('RETRY_') && retryCount < maxRetries) {
                    setTimeout(() => {
                        this.sendTrackingRequest(endpoint, data, eventType, retryCount + 1);
                    }, retryDelay * (retryCount + 1));
                } else {
                    console.error(`[AdvertisementWidget] Failed to track ${eventType}`);
                }
            });
        }
    };

    // Watch for new containers added dynamically
    function checkAndInitContainers() {
        if (AdvertisementWidget.config.initialized) {
            AdvertisementWidget.initContainers();
        }
    }

    if ('MutationObserver' in window) {
        const containerObserver = new MutationObserver(function() {
            if (containerObserver._debounceTimer) {
                clearTimeout(containerObserver._debounceTimer);
            }
            containerObserver._debounceTimer = setTimeout(checkAndInitContainers, 100);
        });

        function startObserving() {
            if (document.body) {
                containerObserver.observe(document.body, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    attributeFilter: ['data-location', 'data-format']
                });
            }
        }

        if (document.body) {
            startObserving();
        } else {
            document.addEventListener('DOMContentLoaded', startObserving);
        }
    }

    window.AdvertisementWidget = AdvertisementWidget;

})(window, document);
